import { useEffect, useState } from "react";

export type WeatherData = {
  location: string;
  updatedAt: string | null;
  current: {
    temperature: number | null;
    apparent: number | null;
    humidity: number | null;
    precipitation: number | null;
    cloudCover: number | null;
    windSpeed: number | null;
    label: string;
  };
  daily: Array<{ date: string; label: string; max: number | null; min: number | null; rain: number | null }>;
};

export type NewsData = {
  source: string;
  items: Array<{ title: string; link: string; source: string; publishedAt: string }>;
};

export type NetworkData = {
  localSnapshot: {
    capturedAt: string;
    host: string;
    primary: {
      interface: string;
      label: string;
      ipv4: string;
      ipv6: Array<{ address: string; kind: string }>;
      gateway: string;
      media: string;
      status: string;
    };
    secondary: Array<{
      interface: string;
      label: string;
      ipv4: string;
      ipv6?: Array<{ address: string; kind: string }>;
      status: string;
    }>;
    dns: Array<string | { address: string; label: string }>;
    tailscale?: {
      self: { ipv4: string; ipv6: string; dnsName: string };
      truenas: { ipv4: string; ipv6: string; dnsName: string; lanUrl: string; tailnetUrl: string };
    };
  };
  edge: {
    ip: string | null;
    colo: string | null;
    country: string | null;
    city: string | null;
    timezone: string | null;
    protocol: string;
  };
  external?: {
    ipv4: string | null;
    ipv6: string | null;
    errors: string[];
  };
};

const fetchJson = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, init);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json() as Promise<T>;
};

const fetchJsonWithTimeout = async <T>(url: string, timeoutMs: number): Promise<T> => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetchJson<T>(url, { cache: "no-store", signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

const fetchExternalIp = async (url: string, family: "ipv4" | "ipv6") => {
  const value = await fetchJsonWithTimeout<{ ip: string }>(url, 4200);
  if (!value.ip) throw new Error(`${family} empty response`);
  return value.ip;
};

type ResourceState = {
  loading: boolean;
  error: string;
};

type ResourceKey = "weather" | "news" | "network";

export function useDashboardData() {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [news, setNews] = useState<NewsData | null>(null);
  const [network, setNetwork] = useState<NetworkData | null>(null);
  const [status, setStatus] = useState<Record<ResourceKey, ResourceState>>({
    weather: { loading: true, error: "" },
    news: { loading: true, error: "" },
    network: { loading: true, error: "" }
  });

  useEffect(() => {
    let alive = true;

    const setResourceStatus = (key: ResourceKey, next: ResourceState) => {
      if (!alive) return;
      setStatus((current) => ({ ...current, [key]: next }));
    };

    fetchJson<WeatherData>("/api/weather")
      .then((value) => {
        if (!alive) return;
        setWeather(value);
        setResourceStatus("weather", { loading: false, error: "" });
      })
      .catch((error: unknown) => {
        setResourceStatus("weather", {
          loading: false,
          error: error instanceof Error ? error.message : "weather fetch failed"
        });
      });

    fetchJson<NewsData>("/api/news")
      .then((value) => {
        if (!alive) return;
        setNews(value);
        setResourceStatus("news", { loading: false, error: "" });
      })
      .catch((error: unknown) => {
        setResourceStatus("news", {
          loading: false,
          error: error instanceof Error ? error.message : "news fetch failed"
        });
      });

    fetchJson<NetworkData>("/api/network")
      .then(async (value) => {
        const errors: string[] = [];
        const [ipv4, ipv6] = await Promise.all([
          fetchExternalIp("https://api.ipify.org?format=json", "ipv4").catch((error: unknown) => {
            errors.push(error instanceof Error ? `IPv4 ${error.message}` : "IPv4 fetch failed");
            return null;
          }),
          fetchExternalIp("https://api6.ipify.org?format=json", "ipv6").catch((error: unknown) => {
            errors.push(error instanceof Error ? `IPv6 ${error.message}` : "IPv6 fetch failed");
            return null;
          })
        ]);
        if (!alive) return;
        setNetwork({ ...value, external: { ipv4, ipv6, errors } });
        setResourceStatus("network", { loading: false, error: "" });
      })
      .catch((error: unknown) => {
        setResourceStatus("network", {
          loading: false,
          error: error instanceof Error ? error.message : "network fetch failed"
        });
      });

    return () => {
      alive = false;
    };
  }, []);

  return { weather, news, network, status };
}
